import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { TicketStatus } from "@/api/tickets";
import { Permissions } from "@/auth/permissions";
import { usePermission } from "@/auth/session-provider";
import { Loader } from "@/components/motion/loader";
import { RequestBoundary } from "@/components/request-boundary";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InternalNoteComposer } from "@/features/tickets/components/internal-note-composer";
import { TicketActivityScroller } from "@/features/tickets/components/ticket-activity-scroller";
import { TicketAttachmentList } from "@/features/tickets/components/ticket-attachment-list";
import { TicketBody } from "@/features/tickets/components/ticket-body";
import { TicketDetailActionsSkeleton, TicketDetailSkeleton } from "@/features/tickets/components/ticket-detail-skeleton";
import { TicketLinkedEntityList } from "@/features/tickets/components/ticket-linked-entity-list";
import { TicketProperties } from "@/features/tickets/components/ticket-properties";
import { TicketResolutionDialog } from "@/features/tickets/components/ticket-resolution-dialog";
import { useTicketDetail } from "@/features/tickets/hooks/use-ticket-detail";
import { useTicketResolution } from "@/features/tickets/hooks/use-ticket-resolution";
import { useIntakeMessages } from "@/features/tickets/intake/intake-messages";
import { TicketIntakeSubmission } from "@/features/tickets/intake/ticket-intake-submission";
import { channelPresentation, formatDateTime, statusPresentation } from "@/features/tickets/utils/ticket-presentation";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useI18n } from "@/locales/i18n-provider";
import { RequestError } from "@/request";
import { cn } from "@/utils";
import { useEmbeddedHost } from "../embedded-host-provider";
import { useUnsavedWork } from "../hooks/use-unsaved-work";
import { embeddedTicketReturnTo } from "./ticket-navigation";

/**
 * 嵌入版工单详情（设计 §5.3、§5.4）。
 *
 * 页面容器与独立工作台分开，只组合下沉到 `features/tickets` 的查询、活动流、附件、
 * 属性栏和回复编辑器。这样嵌入端拥有自己的返回路径与紧凑布局，不依赖完整工作台壳。
 */
export default function EmbeddedTicketDetailPage() {
  const { intlLocale, t } = useI18n();
  const intakeMessages = useIntakeMessages();
  const { can } = usePermission();
  const { businessContext } = useEmbeddedHost();
  const navigate = useNavigate();
  const location = useLocation();
  const { ticketId } = useParams<{ ticketId: string }>();
  const { ticketQuery, ticket, activities, addNote } = useTicketDetail(ticketId);
  const resolution = useTicketResolution(ticketId);
  const [tab, setTab] = useState("activity");
  const [activityScrollToEndRequest, setActivityScrollToEndRequest] = useState(0);
  const [hasUnsavedWork, setHasUnsavedWork] = useState(false);
  const [hasResolutionUnsavedWork, setHasResolutionUnsavedWork] = useState(false);

  const isLargeScreen = useMediaQuery("(min-width: 1024px)");

  useUnsavedWork(hasUnsavedWork || hasResolutionUnsavedWork);

  useEffect(() => {
    // 功能或对象范围 403 是确定性拒绝，进入嵌入错误页；绝不能按 Token 过期处理。
    if (ticketQuery.error instanceof RequestError && ticketQuery.error.status === 403) {
      void navigate("/embedded/access-denied", { replace: true });
    }
  }, [navigate, ticketQuery.error]);

  const status = statusPresentation(ticket?.status ?? 0);
  const channel = channelPresentation(ticket?.channel ?? 0);
  const isResolved = ticket?.status === TicketStatus.Resolved || ticket?.status === TicketStatus.Closed;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden max-w-7xl mx-auto">
      <header className="flex shrink-0 items-center gap-3 px-4 pt-4 pb-2">
        <Button
          aria-label={t("tickets.detail.returnToList")}
          size="sm"
          variant="ghost"
          className="px-2 hover:bg-unset text-muted-foreground"
          onClick={() => navigate(embeddedTicketReturnTo(location, Boolean(businessContext)))}
        >
          <Icon icon="tabler:arrow-left" />
          {t("tickets.detail.returnToList")}
        </Button>
        <div className="min-w-0 flex-1"></div>
        {ticketQuery.isPending ? (
          <TicketDetailActionsSkeleton showStatusAction={can(Permissions.tickets.changeStatus)} />
        ) : (
          <div className="flex items-center gap-1.5">
            {can(Permissions.tickets.changeStatus) && !isResolved ? (
              <Button
                aria-label={t(isResolved ? "tickets.detail.resolvedAria" : "tickets.actions.resolve")}
                className="h-9 px-3"
                disabled={!ticket || ticket.status !== TicketStatus.Open || resolution.isPending}
                onClick={() => ticket && resolution.open(ticket)}
              >
                {resolution.isPending ? (
                  <>
                    <span className="hidden sm:inline">{t("tickets.actions.updating")}</span>
                    <Loader className="text-current" label={t("tickets.detail.statusUpdating")} size={16} />
                  </>
                ) : (
                  <>
                    <Icon icon="tabler:circle-check" />
                    <span className="hidden sm:inline">{t("tickets.actions.resolve")}</span>
                  </>
                )}
              </Button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button aria-label={t("tickets.detail.moreActionsAria")} className="h-9 w-9" size="icon" variant="outline">
                  <Icon icon="tabler:dots-vertical" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  onClick={async () => {
                    await navigator.clipboard.writeText(window.location.href);
                    toast.success(t("tickets.detail.linkCopied"));
                  }}
                >
                  <Icon icon="tabler:link" />
                  {t("tickets.detail.copyLink")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => toast.info(t("tickets.detail.followUnavailable"))}>
                  <Icon icon="tabler:bell" />
                  {t("tickets.detail.follow")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => toast.info(t("tickets.detail.archiveUnavailable"))}>
                  <Icon icon="tabler:archive" />
                  {t("tickets.detail.archive")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto lg:overflow-hidden">
        <RequestBoundary loadingFallback={<TicketDetailSkeleton />} loadingLabel={t("tickets.detail.loading")} query={ticketQuery}>
          {(detail) => (
            <div className="mx-auto grid min-h-full w-full items-start gap-4 px-2 py-3 lg:h-full lg:min-h-0 lg:grid-cols-3 lg:items-stretch">
              <div className="min-h-full gap-0 overflow-hidden py-0 lg:col-span-2 lg:flex lg:h-full lg:min-h-0 lg:flex-col">
                <Tabs className="flex min-h-0 flex-1 flex-col" value={tab} onValueChange={setTab}>
                  <div className="shrink-0 px-4 pb-2">
                    <div className="flex items-center gap-2 text-xs font-semibold text-primary">#{detail.number}</div>
                    <h1 className="mt-1.5 wrap-anywhere text-lg font-semibold leading-snug text-foreground">{detail.subject}</h1>
                    <TicketBody key={detail.id} body={detail.body} className="mt-2 text-sm" />
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Badge className={cn("h-5 px-1.5", status.className)} variant="secondary">
                        <Icon icon={status.icon} />
                        {t(status.labelKey)}
                      </Badge>
                      <span aria-hidden="true" className="text-xs text-muted-foreground">
                        {t("tickets.detail.via")}
                      </span>
                      <Badge variant="outline">
                        <Icon icon={channel.icon} />
                        {t(channel.labelKey)}
                      </Badge>
                      <span aria-hidden="true" className="text-xs text-muted-foreground">
                        {t("tickets.detail.from")}
                      </span>
                      <Badge variant="outline">
                        <Icon icon="tabler:hash" />
                        {detail.projectName}
                      </Badge>
                      <time className="text-xs text-muted-foreground" dateTime={detail.creationTime}>
                        {t("tickets.detail.createdAt", { time: formatDateTime(detail.creationTime, intlLocale) })}
                      </time>
                    </div>
                  </div>

                  <TabsList className="h-10 w-full shrink-0 justify-start gap-3 px-4 text-sm" variant="line">
                    <TabsTrigger className="flex-none px-1" value="activity">
                      <Icon icon="tabler:activity" className="size-4" />
                      {t("tickets.detail.tabs.activities")}
                    </TabsTrigger>
                    <TabsTrigger className="flex-none px-1" value="attachments">
                      <Icon icon="tabler:paperclip" className="size-4" />
                      {t("tickets.detail.tabs.attachments")}
                    </TabsTrigger>
                    <TabsTrigger className="flex-none px-1" value="linked-entities">
                      <Icon icon="tabler:hash" className="size-4" />
                      {t("tickets.detail.tabs.linkedEntities")}
                    </TabsTrigger>
                    <TabsTrigger className="flex-none px-1" value="intake">
                      <Icon icon="tabler:blocks" className="size-4" />
                      {intakeMessages.business}
                    </TabsTrigger>
                    <TabsTrigger className="flex-none px-1 lg:hidden" value="properties">
                      <Icon icon="tabler:ticket" className="size-4" />
                      {t("tickets.detail.tabs.properties")}
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent className="relative mt-0 flex-1 lg:min-h-0 lg:overflow-hidden" value="activity">
                    <TicketActivityScroller activities={activities} scrollToEndRequest={activityScrollToEndRequest} ticketId={detail.id} />
                  </TabsContent>

                  <TabsContent className="mt-0 flex-1 lg:min-h-0 lg:overflow-y-auto" value="attachments">
                    <TicketAttachmentList ticketId={detail.id} />
                  </TabsContent>

                  <TabsContent className="mt-0 flex-1 lg:min-h-0 lg:overflow-y-auto" value="linked-entities">
                    <TicketLinkedEntityList ticketId={detail.id} />
                  </TabsContent>
                  <TabsContent className="mt-0 flex-1 lg:min-h-0 lg:overflow-y-auto" value="intake">
                    <TicketIntakeSubmission ticketId={detail.id} />
                  </TabsContent>

                  {!isLargeScreen ? (
                    <TabsContent className="mt-0 flex-1" value="properties">
                      <TicketProperties ticket={detail} onResolve={() => resolution.open(detail)} />
                    </TabsContent>
                  ) : null}
                </Tabs>

                {can(Permissions.tickets.reply) ? (
                  <div className={cn("shrink-0 px-3 pb-2", tab !== "activity" && "hidden")}>
                    <InternalNoteComposer
                      key={detail.id}
                      ticketId={detail.id}
                      status={detail.status}
                      onUnsavedWorkChange={setHasUnsavedWork}
                      onSubmit={async ({ content, attachmentIds, attachments, mentions }) => {
                        setActivityScrollToEndRequest((current) => current + 1);
                        await addNote.mutateAsync({ attachmentIds, attachments, body: content, mentions });
                      }}
                    />
                  </div>
                ) : null}
              </div>

              {isLargeScreen ? (
                <aside className="h-full min-h-0 overflow-y-auto">
                  <TicketProperties ticket={detail} onResolve={() => resolution.open(detail)} />
                </aside>
              ) : null}
            </div>
          )}
        </RequestBoundary>
      </div>
      <TicketResolutionDialog {...resolution.dialogProps} onUnsavedWorkChange={setHasResolutionUnsavedWork} />
    </div>
  );
}
